import { useEffect, useState } from 'preact/hooks';
import { STORY_CHAPTERS } from '../../content/quests';
import { NPCS } from '../../content/npcs';
import type { AccountSave } from '../../game/account';
import type { CharacterSave } from '../../game/character';
import { CARDS } from '../../game/build';
import { RARITY_COLOR } from '../../game/items';
import {
  MAX_TRACKED,
  type QuestDef,
  type QuestKind,
  abandon,
  allQuests,
  isTracked,
  setTracked,
} from '../../game/quests';
import { openPanel, ui } from '../../ui/app/store';
import { Window } from '../../ui/app/Window';
import {
  Amount,
  Cell,
  Empty,
  TipLayer,
  commit,
  confirmThen,
  fmt,
  toast,
  useCtx,
} from '../economy/ui';
import {
  type JournalRow,
  type JournalTab,
  type QuestStatus,
  journalCounts,
  journalGroups,
  offerLevel,
  questStatus,
  stepViews,
} from './logic';
import { zoneName } from './names';

const KIND_LABEL: Record<QuestKind, string> = {
  main: 'Main Quest',
  side: 'Side Quest',
  world: 'World Quest',
  daily: 'Daily Bounty',
  weekly: 'Weekly Bounty',
};
const STATUS_LABEL: Partial<Record<QuestStatus, string>> = {
  ready: 'Complete',
  available: 'Available',
  locked: 'Locked',
};

const npcName = (id: string): string => (id === 'self' ? 'yourself' : (NPCS[id]?.name ?? id));

function chapterOf(id: string): { id: string; name: string; blurb: string } | null {
  const ch = STORY_CHAPTERS.find((x) => x.quests.includes(id));
  return ch ? { id: ch.id, name: ch.name, blurb: ch.blurb } : null;
}

/** Diamond quest-type badge (colour by kind). */
export function QuestBadge({ kind, size = 10 }: { kind: QuestKind; size?: number }) {
  return (
    <i
      class={`qj-dia k-${kind}`}
      style={{ width: `${size}px`, height: `${size}px` }}
      aria-hidden="true"
    />
  );
}

function TrackBox({ q, c }: { q: QuestDef; c: CharacterSave }) {
  const active = !!c.quests.active[q.id];
  const on = isTracked(c, q.id);
  if (!active) return <span class="qj-track is-off" />;
  return (
    <button
      type="button"
      class={`qj-track${on ? ' is-on' : ''}`}
      title={on ? 'Untrack' : 'Track on the HUD'}
      aria-pressed={on}
      onClick={(e) => {
        e.stopPropagation();
        if (!setTracked(c, q.id, !on))
          toast(`You can track up to ${MAX_TRACKED} quests.`, '#e5a54d');
        commit();
      }}
    />
  );
}

function Row({
  row,
  c,
  sel,
  onSelect,
}: {
  row: JournalRow;
  c: CharacterSave;
  sel: boolean;
  onSelect: () => void;
}) {
  const { quest: q, status } = row;
  const flag = STATUS_LABEL[status];
  return (
    <div
      class={`qj-row k-${q.kind}${sel ? ' is-sel' : ''}${status === 'done' ? ' is-done' : ''}`}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect();
        }
      }}
      role="button"
      tabIndex={0}
      aria-pressed={sel}
    >
      <QuestBadge kind={q.kind} />
      <span class="qj-row-n">{q.name}</span>
      {flag && <span class={`qj-flag s-${status}`}>{flag}</span>}
      <span class="qj-row-l fx-num">Lv {q.level}</span>
      <TrackBox q={q} c={c} />
    </div>
  );
}

function RewardsBlock({ q }: { q: QuestDef }) {
  const r = q.rewards;
  return (
    <div class="qj-rewards">
      {r.xp > 0 && <span class="qj-chip xp">{fmt(r.xp)} XP</span>}
      {!!r.silver && <Amount kind="silver" n={r.silver} />}
      {!!r.gold && <Amount kind="gold" n={r.gold} />}
      {r.items?.map((it) => (
        <span key={it.id} class="qj-item">
          <Cell item={it.id} size={30} count={it.count} />
        </span>
      ))}
      {r.gear && (
        <span class="qj-chip" style={{ color: RARITY_COLOR[r.gear] }}>
          {r.gear[0]?.toUpperCase()}
          {r.gear.slice(1)} equipment
        </span>
      )}
      {!!r.skillPoints && <span class="qj-chip">+{r.skillPoints} skill points</span>}
      {r.card && (
        <span
          class="qj-chip card"
          style={{ color: CARDS[r.card] ? RARITY_COLOR[CARDS[r.card]!.rarity] : undefined }}
        >
          Card: {CARDS[r.card]?.name ?? r.card}
        </span>
      )}
      {!!r.bond && <span class="qj-chip bond">Bond +{r.bond}</span>}
      {r.flags?.some((f) => f.startsWith('hint_')) && (
        <span class="qj-chip hint">Map: Glimmerseed locations</span>
      )}
    </div>
  );
}

function Detail({
  q,
  c,
  onAbandoned,
}: {
  q: QuestDef;
  c: CharacterSave;
  a: AccountSave;
  onAbandoned: () => void;
}) {
  const status = questStatus(c, q);
  const steps = stepViews(c, q);
  const active = status === 'active' || status === 'ready';
  const tracked = isTracked(c, q.id);
  const canAbandon = active && q.kind === 'side';
  const cur = steps.find((s) => s.state === 'current');
  return (
    <section class="qj-detail fx-scroll">
      <header class={`qj-head k-${q.kind}`}>
        <div class="qj-kind">
          <QuestBadge kind={q.kind} size={12} />
          {KIND_LABEL[q.kind]}
          {chapterOf(q.id) && <span class="qj-chapter">· {chapterOf(q.id)!.name}</span>}
        </div>
        <h2 class="qj-title">{q.name}</h2>
        <div class="qj-meta">
          <span>
            Lv <b class="fx-num">{q.level}</b>
          </span>
          <span>{zoneName(q.zone)}</span>
          <span>
            From <b>{npcName(q.giver)}</b>
            {q.turnIn !== q.giver && q.turnIn !== 'self' && (
              <>
                {' '}
                · Report to <b>{npcName(q.turnIn)}</b>
              </>
            )}
          </span>
        </div>
      </header>

      {status === 'ready' && (
        <div class="qj-banner ready">
          {q.turnIn === 'self'
            ? 'Objectives complete.'
            : `Objectives complete — report to ${npcName(q.turnIn)}.`}
        </div>
      )}
      {status === 'available' && (
        <div class="qj-banner avail">Speak with {npcName(q.giver)} to take this quest.</div>
      )}
      {status === 'locked' && (
        <div class="qj-banner locked">
          {offerLevel(q) > c.level ? `Requires level ${offerLevel(q)}.` : 'Not yet available.'}{' '}
          {npcName(q.giver)} will have it for you.
        </div>
      )}

      <div class="qj-desc">
        {q.dialogue.offer.map((line, i) => (
          <p key={i}>{line}</p>
        ))}
        <div class="qj-sig">— {npcName(q.giver)}</div>
      </div>

      <div class="section-h">Objectives</div>
      <ol class="qj-steps">
        {steps.map((s) =>
          s.state === 'future' && status !== 'done' ? null : (
            <li key={s.index} class={`qj-step is-${s.state}`}>
              {s.objectives.map((o, i) => (
                <div key={i} class={`qj-obj${o.done ? ' is-done' : ''}`}>
                  <i class="qj-check" aria-hidden="true" />
                  <span class="qj-obj-t">{o.text}</span>
                  {o.need > 1 && (
                    <span class="qj-obj-n fx-num">
                      {o.have}/{o.need}
                    </span>
                  )}
                  {o.need > 1 && s.state === 'current' && (
                    <span class="qj-obj-bar">
                      <i style={{ transform: `scaleX(${o.have / o.need})` }} />
                    </span>
                  )}
                </div>
              ))}
            </li>
          ),
        )}
        {steps.some((s) => s.state === 'future') && status !== 'done' && (
          <li class="qj-step is-future">
            <div class="qj-obj">
              <i class="qj-check" aria-hidden="true" />
              <span class="qj-obj-t">…</span>
            </div>
          </li>
        )}
        {(active || status === 'done') && q.turnIn !== 'self' && (
          <li
            class={`qj-step ${status === 'ready' ? 'is-current' : status === 'done' ? 'is-done' : 'is-future'}`}
          >
            <div class={`qj-obj${status === 'done' ? ' is-done' : ''}`}>
              <i class="qj-check" aria-hidden="true" />
              <span class="qj-obj-t">Report to {npcName(q.turnIn)}</span>
            </div>
          </li>
        )}
      </ol>
      {cur?.hint && status === 'active' && cur.index > 0 && <p class="qj-hint">{cur.hint}</p>}
      {status === 'done' && (
        <div class="qj-desc qj-epilogue">
          {q.dialogue.complete.map((line, i) => (
            <p key={i}>{line}</p>
          ))}
          <div class="qj-sig">— {npcName(q.turnIn)}</div>
        </div>
      )}

      <div class="section-h">Rewards</div>
      <RewardsBlock q={q} />

      <footer class="qj-actions">
        {active && (
          <button
            class={`btn sm${tracked ? '' : ' btn--gold'}`}
            onClick={() => {
              if (!setTracked(c, q.id, !tracked))
                toast(`You can track up to ${MAX_TRACKED} quests.`, '#e5a54d');
              commit();
            }}
          >
            {tracked ? 'Untrack' : 'Track'}
          </button>
        )}
        {status !== 'done' && (
          <button class="btn sm" onClick={() => openPanel('map', `quest:${q.id}`)}>
            Show on map
          </button>
        )}
        <span class="fx-spacer" />
        {canAbandon && (
          <button
            class="btn sm qj-abandon"
            onClick={() =>
              confirmThen(
                `Abandon “${q.name}”? Its progress will be lost; ${npcName(q.giver)} can give it to you again.`,
                'Abandon',
                () => {
                  abandon(c, q.id);
                  onAbandoned();
                  commit();
                },
              )
            }
          >
            Abandon
          </button>
        )}
      </footer>
    </section>
  );
}

/** Quest journal (J): Main / Side / Completed, details, tracking and abandoning. */
export function QuestsPanel() {
  const ctx = useCtx();
  const arg = ui.panelArg.value;
  const [tab, setTab] = useState<JournalTab>('main');
  const [sel, setSel] = useState<string | null>(null);
  useEffect(() => {
    if (!arg?.startsWith('quest:')) return;
    const id = arg.slice('quest:'.length);
    setSel(id);
    const c = ctx?.c;
    const q = allQuests().find((x) => x.id === id);
    if (!c || !q) return;
    setTab(
      c.quests.done.includes(id)
        ? 'done'
        : q.kind === 'main' || q.kind === 'world'
          ? 'main'
          : 'side',
    );
  }, [arg]);
  if (!ctx) return null;
  const { c, a } = ctx;
  const counts = journalCounts(c);
  const groups = journalGroups(c, tab, { zoneName, chapterOf });
  const rows = groups.flatMap((g) => g.rows);
  const picked = rows.find((r) => r.quest.id === sel) ?? rows[0] ?? null;
  const trackedCount = c.quests.tracked.filter((id) => c.quests.active[id]).length;
  const TABS: { id: JournalTab; label: string; n: number }[] = [
    { id: 'main', label: 'Main', n: counts.main },
    { id: 'side', label: 'Side', n: counts.side },
    { id: 'done', label: 'Completed', n: counts.done },
  ];
  return (
    <Window
      title="Quest Journal"
      width="min(980px, 96vw)"
      height="min(660px, 90vh)"
      className="fx-win qj-win"
    >
      <div class="fx-bar-top">
        <div class="fx-tabs" role="tablist">
          {TABS.map((t) => (
            <button
              key={t.id}
              class="tab"
              role="tab"
              aria-selected={tab === t.id}
              onClick={() => setTab(t.id)}
            >
              {t.label}
              {t.n > 0 && <span class="fx-badge is-gold">{t.n}</span>}
            </button>
          ))}
        </div>
        <span class="fx-spacer" />
        <span class="qj-tracked">
          <span class="fx-h">Tracked</span>{' '}
          <b class="fx-num">
            {trackedCount}/{MAX_TRACKED}
          </b>
        </span>
      </div>
      <div class="fx-rule" />
      <div class="qj-body">
        <nav class="qj-list fx-scroll">
          {groups.length === 0 && (
            <Empty
              title={
                tab === 'done'
                  ? 'No quests completed yet'
                  : tab === 'side'
                    ? 'No side quests'
                    : 'The story rests'
              }
            >
              {tab === 'side'
                ? 'Look for a gold “!” over the heads of people who need help.'
                : tab === 'done'
                  ? 'Finished quests are recorded here.'
                  : 'Speak with the people of the isles to continue.'}
            </Empty>
          )}
          {groups.map((g) => (
            <section key={g.key} class="qj-group">
              <header class="qj-group-h">
                <span class="qj-group-t">{g.title}</span>
                {g.sub && <span class="qj-group-s">{g.sub}</span>}
                <span class="qj-group-n fx-num">{g.rows.length}</span>
              </header>
              {g.rows.map((r) => (
                <Row
                  key={r.quest.id}
                  row={r}
                  c={c}
                  sel={picked?.quest.id === r.quest.id}
                  onSelect={() => setSel(r.quest.id)}
                />
              ))}
            </section>
          ))}
        </nav>
        {picked ? (
          <Detail q={picked.quest} c={c} a={a} onAbandoned={() => setSel(null)} />
        ) : (
          <section class="qj-detail qj-empty">
            <Empty title="Select a quest">Its objectives, story and rewards appear here.</Empty>
          </section>
        )}
      </div>
      <TipLayer />
    </Window>
  );
}
